import React from 'react';
import { useTheme } from '@emotion/react';
import { TabContext } from '@mui/lab';
import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import { OpenInNew, VisibilityOutlined } from '@mui/icons-material';
import { useToast } from 'context/ToastContext';
import { Secondary } from 'ui-components/Buttons';
import { navigateToDomain } from 'utils/index';
import CustomTabs from 'ui-components/CustomTabs';
import { BuildPlansPreview, FetchPlansConfig, FetchPlansRevisions, RestorePlansRevision, UpdatePlansConfig } from 'api/plans_config';
import PlanCardsEditor from './plans/PlanCardsEditor';
import ComparisonEditor from './plans/ComparisonEditor';
// eslint-disable-next-line no-unused-vars
import PageContentEditor from './plans/PageContentEditor';
// eslint-disable-next-line no-unused-vars
import BillingRulesEditor from './plans/BillingRulesEditor';
import SaveBar from './plans/SaveBar';
import PublishDialog from './plans/PublishDialog';
import HistoryPanel, { HistoryButton } from './plans/HistoryPanel';
import PagePreviewDialog from './plans/PagePreviewDialog';
import { changedPaths } from './plans/changedPaths';

// Who published last, the draft preview and the live page link. Hidden for now;
// their dialogs are still wired, so this is the only switch to flip.
const SHOW_PAGE_TOOLBAR = false;

const SECTIONS = [
    { label: 'Plan cards', value: 'plans' },
    { label: 'Comparison table', value: 'comparison' },
    { label: 'Page content', value: 'page' },
    // Billing rules are built and saved by the same flow, but out of the tab
    // strip for now. Uncomment to bring it back, along with its panel below.
    // { label: 'Billing rules', value: 'billing' },
];

// The backend reports problems as a list of sentences that start with the path
// they belong to. Splitting that off lets each editor show the ones that are its.
const toProblem = (message) => {
    const path = String(message).split(' ')[0].replace(/[:,]$/, '');
    return { path, message };
};

const PlansAndPricing = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { addToast } = useToast();

    const { data: config, isLoading, refetch } = FetchPlansConfig();
    const { mutateAsync: savePlansConfig, isLoading: isSaving } = UpdatePlansConfig();
    const { mutateAsync: restoreRevision, isLoading: isRestoring } = RestorePlansRevision();
    const [historyOpen, setHistoryOpen] = React.useState(false);
    const { data: revisions, isLoading: loadingRevisions, refetch: refetchRevisions } = FetchPlansRevisions({ enabled: historyOpen });

    const [draft, setDraft] = React.useState(null);
    const [baseline, setBaseline] = React.useState(null);
    const [section, setSection] = React.useState('plans');
    const [problems, setProblems] = React.useState([]);
    const [publishOpen, setPublishOpen] = React.useState(false);

    // Switching plan has to bring its own scroll reset — otherwise you land
    // halfway down the new plan's feature list, at whatever depth you left the
    // last one. This scrolls the editor's own container — the app shell is
    // position:fixed, so the page body never scrolls and each screen owns its
    // scroll area.
    const editorRef = React.useRef(null);
    const scrollEditorToTop = React.useCallback(() => {
        editorRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const { mutateAsync: buildPreviewAsync } = BuildPlansPreview();

    const buildPreview = React.useCallback(async () => {
        const response = await buildPreviewAsync({
            plans: draft.plans,
            billing: draft.billing,
            ...(draft.comparison ? { comparison: draft.comparison } : {}),
            ...(draft.page ? { page: draft.page } : {}),
        });
        return response?.data?.data;
    }, [buildPreviewAsync, draft]);

    // Loaded once into local state. A background refetch must not overwrite
    // half-finished edits; a clash with another admin is caught on save.
    React.useEffect(() => {
        if (config && !draft) {
            const loaded = {
                plans: config.plans || {},
                billing: config.billing || {},
                comparison: config.comparison || null,
                page: config.page || null,
                subscriptions: config.subscriptions || {},
                updated_at: config.updated_at || null,
            };
            setDraft(loaded);
            setBaseline(JSON.stringify(loaded));
        }
    }, [config, draft]);

    const dirty = Boolean(draft) && JSON.stringify(draft) !== baseline;

    // The same path list the backend records, so the summary, the publish
    // dialog and the history log all describe a change the same way.
    const changes = React.useMemo(() => {
        if (!draft || !baseline) return [];
        const before = JSON.parse(baseline);
        return changedPaths(
            {
                plans: before.plans, billing: before.billing, comparison: before.comparison,
                page: before.page, subscriptions: before.subscriptions,
            },
            {
                plans: draft.plans, billing: draft.billing, comparison: draft.comparison,
                page: draft.page, subscriptions: draft.subscriptions,
            },
        );
    }, [draft, baseline]);

    const changeSummary = React.useMemo(() => {
        const plans = new Set();
        let comparison = 0;
        changes.forEach((path) => {
            if (path.startsWith('plans.')) plans.add(path.split('.')[1]);
            if (path.startsWith('comparison')) comparison += 1;
        });
        const parts = [];
        if (plans.size) parts.push(`${plans.size} plan${plans.size === 1 ? '' : 's'}`);
        if (comparison) parts.push('comparison table');
        if (changes.some((path) => path.startsWith('page'))) parts.push('page content');
        if (changes.some((path) => path.startsWith('billing') || path.startsWith('subscriptions'))) parts.push('billing rules');
        return parts.join(' and ') || `${changes.length} change${changes.length === 1 ? '' : 's'}`;
    }, [changes]);

    const problemsFor = React.useCallback(
        (prefix) => problems.filter((problem) => problem.path.startsWith(prefix)),
        [problems],
    );

    const handleSave = async () => {
        setProblems([]);
        setPublishOpen(false);
        try {
            const response = await savePlansConfig({
                plans: draft.plans,
                billing: draft.billing,
                subscriptions: draft.subscriptions,
                ...(draft.comparison ? { comparison: draft.comparison } : {}),
                ...(draft.page ? { page: draft.page } : {}),
                updated_at: draft.updated_at,
            });
            const saved = response?.data?.data;
            const next = {
                plans: saved?.plans || draft.plans,
                billing: saved?.billing || draft.billing,
                comparison: saved?.comparison ?? draft.comparison,
                page: saved?.page ?? draft.page,
                subscriptions: saved?.subscriptions || draft.subscriptions,
                updated_at: saved?.updated_at || null,
            };
            setDraft(next);
            setBaseline(JSON.stringify(next));
            addToast('Published. The pricing pages show it on their next load.', 'success');
            refetch();
            refetchRevisions();
        } catch (error) {
            const body = error?.response?.data;
            const details = body?.message?.errors || body?.errors;
            if (Array.isArray(details) && details.length) {
                // Held on the page next to the fields rather than thrown away in
                // a toast: there can be several, and each belongs somewhere.
                setProblems(details.map(toProblem));
            } else {
                addToast(body?.message || 'Error: Something went wrong.', 'error');
            }
        }
    };

    const handleDiscard = () => {
        setDraft(JSON.parse(baseline));
        setProblems([]);
    };

    if (isLoading || !draft) {
        return (
            <Grid item xs={12} paddingInline={isSmallScreen ? '1rem' : '2rem'}>
                <Box className='skeleton' minHeight={"60dvh"} sx={{ borderRadius: '8px', border: `1px solid ${theme.palette.customColors.b50}` }} />
            </Grid>
        );
    }

    return (
        <Grid container paddingInline={isSmallScreen ? '1rem' : '2rem'} display="grid" gap="1rem">
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <TabContext value={section}>
                    <CustomTabs
                        data={SECTIONS}
                        value={section}
                        isSmallScreen={isSmallScreen}
                        handleChange={(event, value) => setSection(value)}
                        titleStyle={{ fontSize: '1rem' }}
                        tabStyle={{ padding: isSmallScreen ? '0.5rem 1rem' : '0rem 1.5rem 0.75rem' }}
                        bgColor={theme.palette.customColors.yellow_bg}
                    />
                </TabContext>

                {SHOW_PAGE_TOOLBAR && (
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    {config?.metadata?.last_updated_by && (
                        <Typography sx={{ fontSize: '0.8125rem', color: theme.palette.customColors.g200 }}>
                            Last published by {config.metadata.last_updated_by}
                        </Typography>
                    )}
                    {/* The exact preview: the marketing page itself, rendered from
                        the draft in a frame. The card beside the fields is drawn by
                        hand in this app's own components, so it can only ever
                        approximate the real one — this is the answer to "what will
                        customers actually see". */}
                    <Secondary
                        startIcon={<VisibilityOutlined />}
                        sx={{ padding: '0.375rem 1rem' }}
                        onClick={() => setPreviewOpen(true)}
                    >
                        Preview page
                    </Secondary>
                    <Secondary
                        startIcon={<OpenInNew />}
                        sx={{ padding: '0.375rem 1rem' }}
                        onClick={() => window.open(navigateToDomain('main', 'pricing'), '_blank')}
                    >
                        View live page
                    </Secondary>
                    {/* History, hidden for now; its dialog below is still wired.
                    <HistoryButton onClick={() => setHistoryOpen(true)} />
                    */}
                </Box>
                )}
            </Box>

            {/* Scrolls with the page rather than inside a scroller of its own: a
                fixed-height panel meant two things scrolled at once, and a long
                plan hid its own save bar. The bar is sticky, so it stays in view
                without the panel having to reserve room for it. */}
            <Box ref={editorRef} sx={{ width: '100%', scrollMarginTop: '1rem', maxHeight: `calc(100dvh - ${dirty || problems.length ? '22rem' : '18rem'})`, height: '100%', overflow: 'auto', scrollBehavior: 'smooth', scrollbarWidth: 'thin', '&::-webkit-scrollbar': { width: '0.5rem' }, '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.customColors.b100, borderRadius: '8px' } }}>
                <Box paddingBottom="0.5rem">
                    {section === 'plans' && (
                        <PlanCardsEditor
                            plans={draft.plans}
                            onPlanSelected={scrollEditorToTop}
                            billing={draft.billing}
                            publishedPlans={baseline ? JSON.parse(baseline).plans : null}
                            problemsFor={problemsFor}
                            onChange={(plans) => setDraft({ ...draft, plans })}
                        />
                    )}
                    {section === 'comparison' && (
                        <ComparisonEditor
                            comparison={draft.comparison}
                            plans={draft.plans}
                            problemsFor={problemsFor}
                            onChange={(comparison) => setDraft({ ...draft, comparison })}
                        />
                    )}
                    {section === 'page' && (
                        <PageContentEditor
                            page={draft.page}
                            onChange={(page) => setDraft({ ...draft, page })}
                        />
                    )}
                    {/* Hidden with its tab. The draft still carries billing,
                        so what is stored is untouched either way.
                    {section === 'billing' && (
                        <BillingRulesEditor
                            billing={draft.billing}
                            subscriptions={draft.subscriptions}
                            onChange={({ billing, subscriptions }) => setDraft({ ...draft, billing, subscriptions })}
                        />
                    )}
                    */}
                </Box>
            </Box>

            <SaveBar
                dirty={dirty}
                saving={isSaving}
                summary={changeSummary}
                problems={problems}
                onSave={() => setPublishOpen(true)}
                onDiscard={handleDiscard}
            />

            <PublishDialog
                open={publishOpen}
                handleClose={() => setPublishOpen(false)}
                changes={changes}
                plans={draft.plans}
                saving={isSaving}
                onConfirm={handleSave}
            />

            <PagePreviewDialog
                open={previewOpen}
                handleClose={() => setPreviewOpen(false)}
                draft={draft}
                buildPreview={buildPreview}
            />

            <HistoryPanel
                open={historyOpen}
                handleClose={() => setHistoryOpen(false)}
                revisions={revisions || []}
                loading={loadingRevisions}
                restoring={isRestoring}
                onRestore={async (revision) => {
                    try {
                        const response = await restoreRevision({ _id: revision._id });
                        const saved = response?.data?.data;
                        const next = {
                            plans: saved?.plans || {},
                            billing: saved?.billing || {},
                            comparison: saved?.comparison ?? null,
                            page: saved?.page ?? null,
                            subscriptions: saved?.subscriptions || {},
                            updated_at: saved?.updated_at || null,
                        };
                        setDraft(next);
                        setBaseline(JSON.stringify(next));
                        setHistoryOpen(false);
                        addToast('Restored. The pricing pages show that version on their next load.', 'success');
                        refetch();
                        refetchRevisions();
                    } catch (error) {
                        addToast(error?.response?.data?.message || 'Could not restore that version.', 'error');
                    }
                }}
            />
        </Grid>
    );
};

export default PlansAndPricing;
